// The roulette screen (casino.md §4.1, ECONOMY · CASINO).
//
// One `GameView` of `gameview.ts`: the module answers with markup and it
// takes one released button. It owns NO element, no listener and no
// clock. `CasinoPanel` writes the string and hands the release over.
//
// **The server rules on every bet.** The screen builds one bet out of one
// or two taps and sends it. It never says a bet won, never pays and never
// counts a pocket: the adjacency below decides what the felt HIGHLIGHTS,
// and `casino/roulette.go` decides what the table accepts (casino.md
// §4.1).
//
// **The only local state is the bet the reader is building.** The chip
// size, the armed inside kind, the first tap of a two-tap bet and the one
// notice a wrong tap raised. Everything the wire has seen — the bets that
// stand, the phase, the window and the called number — comes from
// `GameCtx.slice` and from nowhere else.
//
// **No sentence is a literal and no figure is written here.** Every word
// is a key of `gen/strings.ts` and every figure comes from the hello
// catalog's `CasinoRules` (R355): the minimum bet of the tier, what a
// kind pays and how many pockets the wheel holds.
//
// **Two taps, because a finger has no lines.** A classic felt takes an
// inside bet on the line between two numbers, which a 40 px cell cannot
// offer. So the reader arms a kind first and then names the numbers: a
// straight in one tap, a split, a corner and a line in two, and a street
// in one tap on the row. A second tap that does not touch the first
// raises `roulette_not_adjacent` and gives the pick back.

import { CASINO_BET_KINDS, CASINO_POCKETS } from '../../gen/balance';
import {
  CASINO_CHIP_STEPS, CASINO_HUB_BADGE_PX, CASINO_SEAT_BADGE_PX, CASINO_WHEEL_MS,
} from '../../gen/presentation';
import {
  CasinoAction, CasinoGame, CasinoPhase,
  type CasinoBet, type CasinoResult, type CasinoRules, type CasinoTable,
} from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { groupThousands } from '../../locale/group';
import { armedCtl, ctl, seatName } from './cards';
import { clubOf, factionBadge } from './club';
import { casinoActLeftSec, casinoOffers, casinoOwnRows, tableMinBet } from './casinostate';
import { part, type GameCtx, type GameView } from './gameview';
import type { MapKeyId } from '../../gen/presentation';
import { keyTitle } from './help';
import { tagSpeech, resultTag, handWord, sayAttrs } from './moment';
import {
  DOUBLE_ZERO, lastPart, pocketColour, pocketLabel, rimPart, wheelPart, type RimSeat,
} from './wheel';

/**
 * The arm key of CLEAR, as `Overlay.armKey` builds it: the button's
 * `data-act` and its `data-id`, joined by a colon. The label and the
 * armed class are drawn from the same key the panel hands to `act`, so
 * the two halves of the two-press gate can never disagree (R837).
 */
const CLEAR_KEY = 'casino:clear';

/**
 * The chip sizes, as multiples of the tier's minimum bet.
 *
 * The figures are multiples and not credits: the minimum bet belongs to
 * `CasinoRules.stakes` and reaches the reader from the hello catalog, so
 * one row of pills serves all three stake tiers. The multiples themselves
 * belong to `data/presentation/presentation.toml`, so the felt and the
 * blackjack bar offer one row of chips (R988).
 */
const CHIP_STEPS = CASINO_CHIP_STEPS;

/** The index of one bet kind in the wire's own order. */
function kindOf(code: string): number {
  return CASINO_BET_KINDS.findIndex((k) => k.code === code);
}

const K_STRAIGHT = kindOf('straight');
const K_SPLIT = kindOf('split');
const K_STREET = kindOf('street');
const K_CORNER = kindOf('corner');
const K_LINE = kindOf('line');
const K_DOZEN = kindOf('dozen');
const K_COLUMN = kindOf('column');

/** The label of every bet kind, by its wire code. */
const KIND_LABEL: Readonly<Record<string, string>> = {
  straight: S.roulette_straight,
  split: S.roulette_split,
  street: S.roulette_street,
  corner: S.roulette_corner,
  line: S.roulette_line,
  dozen: S.roulette_dozen,
  column: S.roulette_column,
  red: S.roulette_red,
  black: S.roulette_black,
  odd: S.roulette_odd,
  even: S.roulette_even,
  low: S.roulette_low,
  high: S.roulette_high,
};

/** The word of a pocket's colour: each zero wears its own word. */
function pocketWord(n: number): string {
  if (n === DOUBLE_ZERO) return S.roulette_double_zero;
  const col = pocketColour(n);
  return col === 'zero' ? S.roulette_zero : KIND_LABEL[col];
}

/** One kind as a pill or a button carries it. */
interface KindRow {
  kind: number;
  label: string;
}

function kindRow(code: string): KindRow {
  return { kind: kindOf(code), label: KIND_LABEL[code] };
}

/**
 * The sentence that says a kind takes two taps, BEFORE the first tap
 * (R2143): a reader who armed SPLIT reads how to place it, and does not
 * learn it from the notice after the first tap.
 */
const TWO_TAPS: ReadonlyMap<number, string> = new Map([
  [K_SPLIT, S.roulette_two_split],
  [K_CORNER, S.roulette_two_corner],
  [K_LINE, S.roulette_two_line],
]);

/** The five inside kinds, in the order the pills carry them. */
const INSIDE: readonly KindRow[] = ['straight', 'split', 'street', 'corner', 'line'].map(kindRow);

/** The six even-money bets. The index is the `data-n` of the button. */
const EVENS: readonly KindRow[] = ['red', 'black', 'odd', 'even', 'low', 'high'].map(kindRow);

/**
 * The order the felt draws the six even-money bets in, as indexes of
 * `EVENS`: low, even, red, black, odd, high, from the top of the cloth
 * down, as a classic felt runs beside its numbers (R2117).
 */
const EVEN_ORDER: readonly number[] = [4, 3, 0, 1, 2, 5];

// --- the felt's geometry ----------------------------------------------

/**
 * The street one number stands in, from 1 to 12. A zero stands in none,
 * so it answers 0.
 *
 * The felt is three columns of twelve rows: row r holds 3r-2, 3r-1 and
 * 3r, and the zero and the double zero stand side by side above them.
 */
export function rowOf(n: number): number {
  return n >= 1 && n <= 36 ? Math.ceil(n / 3) : 0;
}

/** The column one number stands in, from 1 to 3. A zero answers 0. */
function colOf(n: number): number {
  return n >= 1 && n <= 36 ? ((n - 1) % 3) + 1 : 0;
}

/** The pockets each zero touches on the felt (casino.md §4.1, R2752). */
const ZERO_TOUCH: ReadonlyMap<number, readonly number[]> = new Map([
  [0, [1, 2, DOUBLE_ZERO]],
  [DOUBLE_ZERO, [2, 3, 0]],
]);

/**
 * True when two numbers touch on the felt.
 *
 * Two numbers of one street touch when they stand beside each other, and
 * two numbers of one column touch when their streets are neighbours. The
 * zero touches 1, 2 and the double zero, and the double zero touches 2, 3
 * and the zero, which is where the felt puts them.
 */
export function adjacent(a: number, b: number): boolean {
  if (a === b) return false;
  if (!inRange(a) || !inRange(b)) return false;
  const zero = ZERO_TOUCH.get(a) ?? ZERO_TOUCH.get(b);
  if (zero) return zero.includes(ZERO_TOUCH.has(a) ? b : a);
  if (rowOf(a) === rowOf(b)) return Math.abs(a - b) === 1;
  return Math.abs(a - b) === 3;
}

/** The three numbers of the street one number stands in, or null. */
export function streetOf(n: number): number[] | null {
  const r = rowOf(n);
  if (r === 0) return null;
  return [r * 3 - 2, r * 3 - 1, r * 3];
}

/**
 * The four numbers of the corner two taps name, or null.
 *
 * A corner needs two numbers that meet at one point: their streets are
 * neighbours and their columns are neighbours. The answer starts at the
 * number in the upper street and the left column.
 */
export function cornerOf(a: number, b: number): number[] | null {
  const ra = rowOf(a); const rb = rowOf(b);
  const ca = colOf(a); const cb = colOf(b);
  if (ra === 0 || rb === 0) return null;
  if (Math.abs(ra - rb) !== 1 || Math.abs(ca - cb) !== 1) return null;
  const top = Math.min(ra, rb);
  const left = Math.min(ca, cb);
  const tl = (top - 1) * 3 + left;
  return [tl, tl + 1, tl + 3, tl + 4];
}

/**
 * The six numbers of the line two taps name, or null.
 *
 * A line is two streets that touch, so the taps only have to fall in
 * neighbouring streets. The column of either tap says nothing.
 */
export function lineOf(a: number, b: number): number[] | null {
  const ra = rowOf(a); const rb = rowOf(b);
  if (ra === 0 || rb === 0) return null;
  if (Math.abs(ra - rb) !== 1) return null;
  const top = Math.min(ra, rb);
  const first = (top - 1) * 3 + 1;
  return [first, first + 1, first + 2, first + 3, first + 4, first + 5];
}

function inRange(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= DOUBLE_ZERO;
}

/**
 * What an OUTSIDE bet carries on the wire (`casino/roulette.go`).
 *
 * The engine names an outside bet by its kind, and it refuses a list of
 * pockets on one. A dozen and a column need a selector, so each carries
 * ONE number: the index 1, 2 or 3. Every other outside kind — red,
 * black, odd, even, low and high — carries an empty list, because the
 * kind alone already says which half of the felt it takes.
 *
 * The span `coverText` derives from the index is therefore a picture and
 * never a bet: it gives the button its span and the list its line.
 */
const NO_NUMBERS: number[] = [];

// --- the bets that stand ----------------------------------------------

/** What every bet of the reader's puts on one pocket, added up. */
export function ridesOn(bets: readonly CasinoBet[], n: number): number {
  let sum = 0;
  for (const b of bets) {
    // The inside kinds alone name their pockets. An outside bet carries
    // an index or nothing, and it covers a third or a half of the felt,
    // so it would pile a figure onto every cell and say nothing. The
    // cell counts the inside money; the list counts every bet one time.
    if (!CASINO_BET_KINDS[b.kind]?.inside) continue;
    if (b.numbers.includes(n)) sum += b.amount;
  }
  return sum;
}

/**
 * What one exact bet holds: one kind on one bet target.
 *
 * `numbers` is the target the wire carries and not the cover, so a dozen
 * and a column are asked for with their index, and an even-money kind
 * with an empty list.
 */
export function stackOn(
  bets: readonly CasinoBet[], kind: number, numbers: readonly number[],
): number {
  let sum = 0;
  for (const b of bets) {
    if (b.kind !== kind) continue;
    if (b.numbers.length !== numbers.length) continue;
    let same = true;
    for (let i = 0; i < numbers.length; i++) {
      if (b.numbers[i] !== numbers[i]) { same = false; break; }
    }
    if (same) sum += b.amount;
  }
  return sum;
}

/** Everything the reader has on the felt. */
export function betTotal(bets: readonly CasinoBet[]): number {
  let sum = 0;
  for (const b of bets) sum += b.amount;
  return sum;
}

/** A whole figure of credits, grouped the way every figure is (R687). */
function amount(n: number): string {
  return groupThousands(Math.max(0, Math.floor(n)));
}

/** The pockets of the wheel the catalog states, or the compile-time
 *  count before the catalog arrived. */
function pocketsOf(rules: CasinoRules | null): number {
  return rules?.roulettePockets || CASINO_POCKETS;
}

/** What one kind pays to one, which the catalog states. */
function paysOf(rules: CasinoRules | null, kind: number): number {
  for (const p of rules?.roulette ?? []) if (p.kind === kind) return p.pays;
  return 0;
}

// --- the screen -------------------------------------------------------

/** The two-tap bet the reader is building, before the wire sees it. */
interface Pick {
  kind: number;
  first: number;
}

export class RouletteView implements GameView {
  /** Which chip the reader holds, as an index of `CHIP_STEPS`. */
  private chip = 0;
  /** Which inside kind is armed, as an index of `INSIDE`. */
  private inside = 0;
  /** The first tap of a two-tap bet, while the second one waits. */
  private pick: Pick | null = null;
  /**
   * The one notice a wrong tap raised. It is a key of the string table
   * and never a sentence, so the language is picked once (R855).
   */
  private notice: 'none' | 'not-adjacent' = 'none';
  /** The hand the local state belongs to. A new hand gives it back. */
  private handId = 0;
  /**
   * The hand before that one.
   *
   * The table walks BETTING to SPINNING and back to BETTING with a NEW
   * hand id, and it never stops at SETTLED, so the result of the spin
   * belongs to the hand before the window the reader now reads. The
   * called row therefore takes the current hand OR this one (R988).
   */
  private prevHandId = 0;
  /** The phase of the last frame this screen drew, to see a change. */
  private lastPhase: CasinoPhase = CasinoPhase.CASINO_PHASE_UNSPECIFIED;
  /** The `performance.now()` at which the wheel of this hand started. */
  private spinAtMs = 0;
  /**
   * The bets of this hand as the last open window showed them, and the
   * bets of the last hand that held any. REBET places the second list
   * again (R2117). Both are copies of wire frames, never a bet the screen
   * made up.
   */
  private standing: CasinoBet[] = [];
  private lastHand: CasinoBet[] = [];
  /** The hand REBET was pressed in, so one window takes one rebet. */
  private rebetHand = -1;

  html(ctx: GameCtx): string {
    const table = ctx.slice.table;
    if (!table) return '';
    this.syncHand(table);
    this.syncPhase(table, ctx);
    const open = table.phase === CasinoPhase.CASINO_PHASE_BETTING;
    const spinning = table.phase === CasinoPhase.CASINO_PHASE_SPINNING;
    const settled = table.phase === CasinoPhase.CASINO_PHASE_SETTLED;
    const bets = table.yourBets;
    const chipAmt = this.chipAmount(table, ctx.rules);
    // Every control is dead while the wheel runs and while the hand
    // settles: the window is the server's and it has shut.
    const live = open && ctx.rules !== null && chipAmt > 0;
    // The spin this screen still shows, which is the current hand's or
    // the one before it. The wheel reads it at once, so the ball travels
    // to the right pocket; the called row waits for the ball to land.
    const res = this.spinResult(ctx, table);
    const called = res !== null && this.ballDown(ctx, table) ? res : null;
    // The wheel stands at the TOP of the felt, above the number grid: a
    // reader on a telephone must not scroll to watch the spin the reader
    // paid for. The grid keeps its place and goes dim instead, because a
    // grid that moved would put a cell under a thumb that aimed at
    // another one (R988).
    //
    // The wheel stands for the spin ALONE (R2117). In the betting window
    // one line takes its place, so the grid starts near the top of a
    // telephone: the called row of the spin before while it stands, then
    // the LAST strip, which is one line as well, so the first bet moves no
    // cell.
    //
    // The wheel stands in its stage with the casino seats on its rim and
    // the club badge at its hub (R2165): a part of its own beside the
    // wheel, so a seat that comes or goes never stops the ball.
    //
    // The casino pane of a desktop has the room the telephone lacks
    // (R2198): the wheel stage stands beside the cloth at every phase, so
    // no cell moves when the window shuts. In the betting window the wheel
    // rests on the pocket it called last and makes no travel, so a rewrite
    // of the table never plays a spin twice; the travel starts with the
    // spin. The called row or the LAST strip stands under the stage.
    let head = '';
    if (ctx.wide) {
      const turn = spinning || settled;
      head = '<div class="rl-stage"><div class="wheel-stage">'
        + `${this.wheel(ctx, table, res, !turn)}${rimOf(ctx, table)}</div>`
        + (turn || res !== null ? calledRow(ctx, table, called) : this.lastStrip(ctx, table))
        + '</div>';
    }
    else if (spinning || settled) {
      head = `<div class="wheel-stage">${this.wheel(ctx, table, res)}${rimOf(ctx, table)}</div>`
        + calledRow(ctx, table, called);
    }
    else if (res !== null) head = calledRow(ctx, table, called);
    else head = this.lastStrip(ctx, table);
    // The window line and the stake line stand in ONE pinned block at the
    // top of the felt, so the time left, the total and CLEAR stay on the
    // screen while the reader scrolls down to the outside bets (R2117).
    return '<div class="casino-felt felt roulette"><div class="rl-pin">'
      + this.stateBar(ctx, open, spinning, settled)
      + this.stakeBar(ctx, table, open)
      + '</div>'
      + head
      + this.chipRow(ctx, table, open)
      + this.kindRow(ctx, open)
      + this.grid(ctx, bets, live, !open, called ? ctx.slice.resultSeq : 0, called?.rouletteNumber ?? -1)
      + this.betList(table, bets, open)
      + '</div>';
  }

  /**
   * One released button of this screen.
   *
   * The screen refuses every press outside the betting window, because a
   * frame can arrive between the press and this line. The server refuses
   * the same bet, so the gate here saves the trip and never rules.
   */
  act(op: string, btn: HTMLButtonElement, key: string, ctx: GameCtx): boolean {
    const table = ctx.slice.table;
    if (!table) return false;
    this.syncHand(table);
    const n = Number(btn.dataset.n ?? 0);
    if (op === 'chip') {
      if (n >= 0 && n < CHIP_STEPS.length) this.chip = n;
      return true;
    }
    if (op === 'kind') {
      if (n >= 0 && n < INSIDE.length) {
        this.inside = n;
        this.pick = null;
        this.notice = 'none';
      }
      return true;
    }
    if (op === 'clear') {
      this.clear(table, key, ctx);
      return true;
    }
    const open = table.phase === CasinoPhase.CASINO_PHASE_BETTING;
    if (op === 'rebet') {
      this.rebet(table, ctx);
      return true;
    }
    if (op === 'takeback') {
      this.takeBack(table, n, ctx);
      return true;
    }
    const amt = this.chipAmount(table, ctx.rules);
    if (op === 'cell') {
      if (open) this.tapCell(n, amt, ctx);
      return true;
    }
    // A dozen and a column name themselves by their index, and the six
    // even-money bets name no pocket at all. The engine refuses a list of
    // pockets on either kind.
    if (op === 'dozen' || op === 'column') {
      if (open && n >= 1 && n <= 3) this.place(op === 'dozen' ? K_DOZEN : K_COLUMN, [n], amt, ctx);
      return true;
    }
    if (op === 'even') {
      const row = EVENS[n];
      if (open && row) this.place(row.kind, [], amt, ctx);
      return true;
    }
    return false;
  }

  /**
   * The casino keybindings (R2143): REBET, the take-back of the last bet
   * and CLEAR, which takes two presses as its button does. Each key takes
   * the road of its button, and answers false where the button is dead,
   * so the press falls through.
   *
   * The spin belongs to the server's own clock — the window shuts and the
   * wheel runs, and no client asks for it — so the `casino_spin` binding
   * is the slot machine's alone.
   */
  key(id: MapKeyId, ctx: GameCtx): boolean {
    const table = ctx.slice.table;
    if (!table) return false;
    this.syncHand(table);
    switch (id) {
      case 'casino_rebet': return this.rebet(table, ctx);
      case 'casino_take_back': return this.takeBack(table, table.yourBets.length - 1, ctx);
      case 'casino_clear': return this.clear(table, CLEAR_KEY, ctx);
      default: return false;
    }
  }

  /** CLEAR: every bet off the felt, on the second press of the gate. */
  private clear(table: CasinoTable, key: string, ctx: GameCtx): boolean {
    if (table.phase !== CasinoPhase.CASINO_PHASE_BETTING || table.yourBets.length === 0) return false;
    if (!ctx.press(key)) return true;
    this.pick = null;
    this.notice = 'none';
    ctx.send({ kind: 'act', action: CasinoAction.CASINO_ACTION_CLEAR_BETS });
    return true;
  }

  /** REBET: the bets of the last hand that held any, once a window. */
  private rebet(table: CasinoTable, ctx: GameCtx): boolean {
    if (!this.canRebet(table, ctx)) return false;
    this.rebetHand = table.handId;
    for (const b of this.lastHand) this.place(b.kind, b.numbers.slice(), b.amount, ctx);
    return true;
  }

  /**
   * One bet off the felt (R2117). The row names its bet by its place in
   * the frame's list, and the intent names it the way the wire states it:
   * the kind, the target and the amount. The server takes back the last
   * bet that carries all three. The key names the last bet of the list.
   */
  private takeBack(table: CasinoTable, n: number, ctx: GameCtx): boolean {
    const b = table.yourBets[n];
    if (table.phase !== CasinoPhase.CASINO_PHASE_BETTING || !b
      || !casinoOffers(table, CasinoAction.CASINO_ACTION_TAKE_BACK_BET)) return false;
    ctx.send({
      kind: 'act',
      action: CasinoAction.CASINO_ACTION_TAKE_BACK_BET,
      amount: b.amount,
      betKind: b.kind,
      betNumbers: b.numbers.slice(),
    });
    return true;
  }

  /**
   * A new hand throws the half-built bet away.
   *
   * A pick that outlived its own window would send a bet the reader made
   * for another spin. Both doors of the screen call this, because a tap
   * can reach `act` before the panel has drawn the new hand one time.
   */
  private syncHand(table: CasinoTable): void {
    if (table.handId !== this.handId) {
      this.prevHandId = this.handId;
      this.handId = table.handId;
      this.pick = null;
      this.notice = 'none';
      // The hand that ended is what REBET places again. A hand the reader
      // sat out keeps the list of the hand before it.
      if (this.standing.length > 0) this.lastHand = this.standing;
      this.standing = [];
    }
    // The open window is the one place the list is read, because a bet
    // can only change inside it.
    if (table.phase === CasinoPhase.CASINO_PHASE_BETTING) {
      this.standing = table.yourBets.slice();
    }
  }

  /**
   * True when REBET can place the bets of the hand before: the window is
   * open, the felt holds no bet of this hand yet, this window took no
   * rebet, and the credits pay the whole list (R2117). A rebet on a felt
   * that already holds bets would double them on a second press.
   */
  private canRebet(table: CasinoTable, ctx: GameCtx): boolean {
    return table.phase === CasinoPhase.CASINO_PHASE_BETTING
      && ctx.rules !== null
      && table.yourBets.length === 0
      && this.lastHand.length > 0
      && this.rebetHand !== table.handId
      && betTotal(this.lastHand) <= ctx.credits;
  }

  /**
   * The window shut, or the wheel started.
   *
   * A pick the reader started in a window that has shut names a bet that
   * spin cannot take, so the screen gives the pick back and arms the
   * plain STRAIGHT again. The stamp is the moment the wheel began to
   * turn, which is what the called row waits on.
   */
  private syncPhase(table: CasinoTable, ctx: GameCtx): void {
    const phase = table.phase;
    if (phase === this.lastPhase) return;
    const was = this.lastPhase;
    this.lastPhase = phase;
    if (was === CasinoPhase.CASINO_PHASE_BETTING
      && phase !== CasinoPhase.CASINO_PHASE_BETTING) {
      this.pick = null;
      this.notice = 'none';
      this.inside = 0;
    }
    if (phase === CasinoPhase.CASINO_PHASE_SPINNING) this.spinAtMs = ctx.nowMs;
  }

  /**
   * The spin this screen still shows, or null.
   *
   * The table walks BETTING, SPINNING and back to BETTING and it never
   * stops at SETTLED, so a screen that waited for that phase showed no
   * called number at all. The row therefore stands from the spin, through
   * the whole of the next window, until the reader puts the first bet of
   * the new hand on the felt.
   */
  private spinResult(ctx: GameCtx, table: CasinoTable): CasinoResult | null {
    const res = ctx.slice.result;
    if (!res || res.tableId !== table.id) return null;
    if (res.handId !== table.handId && res.handId !== this.prevHandId) return null;
    if (table.phase === CasinoPhase.CASINO_PHASE_BETTING
      && table.yourBets.length > 0) {
      return null;
    }
    return res;
  }

  /**
   * True when the ball has come to rest.
   *
   * The wheel takes `CASINO_WHEEL_MS` of the spin phase, and a number the
   * reader read before the ball stopped would give the spin away. Under
   * reduced motion the ball is on its pocket at once, so the row is too
   * (R541).
   */
  private ballDown(ctx: GameCtx, table: CasinoTable): boolean {
    if (ctx.still) return true;
    if (table.phase !== CasinoPhase.CASINO_PHASE_SPINNING) return true;
    return ctx.nowMs - this.spinAtMs >= CASINO_WHEEL_MS;
  }

  // --- the taps -------------------------------------------------------

  /** One tap on a number cell, which the armed kind gives a meaning. */
  private tapCell(n: number, amt: number, ctx: GameCtx): void {
    if (!inRange(n)) return;
    const row = INSIDE[this.inside];
    if (!row) return;
    this.notice = 'none';
    // A straight takes one tap, and so does a street: the cell names the
    // number, and the street the number stands in.
    if (row.kind === K_STRAIGHT) { this.place(K_STRAIGHT, [n], amt, ctx); return; }
    if (row.kind === K_STREET) {
      const street = streetOf(n);
      if (street) this.place(K_STREET, street, amt, ctx);
      return;
    }
    // The three two-tap kinds. The first tap marks the cell and the
    // second one builds the bet, or gives the pick back.
    const pick = this.pick;
    if (!pick || pick.kind !== row.kind) {
      this.pick = { kind: row.kind, first: n };
      return;
    }
    if (n === pick.first) { this.pick = null; return; }
    const numbers = row.kind === K_SPLIT
      ? (adjacent(pick.first, n) ? [Math.min(pick.first, n), Math.max(pick.first, n)] : null)
      : row.kind === K_CORNER ? cornerOf(pick.first, n) : lineOf(pick.first, n);
    this.pick = null;
    if (!numbers) { this.notice = 'not-adjacent'; return; }
    this.place(row.kind, numbers, amt, ctx);
  }

  /** One bet of `amt` credits on the wire. The server rules on it. */
  private place(kind: number, numbers: number[], amt: number, ctx: GameCtx): void {
    if (kind < 0 || amt <= 0 || amt > ctx.credits) return;
    ctx.send({
      kind: 'act',
      action: CasinoAction.CASINO_ACTION_PLACE_BET,
      amount: amt,
      betKind: kind,
      betNumbers: numbers,
    });
  }

  /** What the held chip is worth in credits, at this table's tier. */
  private chipAmount(table: CasinoTable, rules: CasinoRules | null): number {
    return tableMinBet(rules, table) * CHIP_STEPS[this.chip];
  }

  // --- the markup -----------------------------------------------------

  /**
   * The window's own line: what the felt is for, and how long it stays
   * open.
   *
   * The countdown is stated in whole seconds, so the markup changes about
   * one time a second inside a render pass that runs at 4 Hz
   * (ui-map-interface.md §10.3).
   */
  private stateBar(
    ctx: GameCtx, open: boolean, spinning: boolean, settled: boolean,
  ): string {
    let head = S.roulette_bets_shut;
    if (open) head = S.roulette_place_bet;
    else if (spinning) head = S.roulette_wheel_runs;
    else if (settled) head = S.casino_hand_settled;
    const left = Math.ceil(casinoActLeftSec(ctx.slice, ctx.nowMs));
    const clock = open && left > 0
      ? `<span class="left">${S.casino_turn_left({ sec: left })}</span>` : '';
    // The countdown steps one time a second, so the whole bar is a SMALL
    // part of its own: the wheel beside it is never touched (R988). It
    // holds no button either, because a rewrite under a finger would lose
    // the release.
    return part('state', 'class="casino-bar state"',
      `<span class="head">${head}</span>${clock}${this.noticeLine(open)}`);
  }

  /**
   * The stake line of the pinned block: what stands on the felt, REBET
   * and CLEAR (R2117). It changes with a bet and never with the clock, so
   * its buttons stand still under a finger.
   */
  private stakeBar(ctx: GameCtx, table: CasinoTable, open: boolean): string {
    const bets = table.yourBets;
    const rebet = ctl('rebet', S.roulette_rebet, open && this.canRebet(table, ctx), 'rebet',
      keyTitle(S.roulette_rebet, 'casino_rebet'));
    const clear = armedCtl('clear', 'clear', ctx.armLabel(CLEAR_KEY, S.roulette_clear, S.roulette_clear_armed),
      ctx.armed(CLEAR_KEY), 'clear', keyTitle(S.roulette_clear, 'casino_clear') + (open && bets.length ? '' : ' disabled'));
    return part('stake', 'class="casino-bar stake"',
      `<span class="total">${S.roulette_your_bets({ amount: amount(betTotal(bets)) })}</span>`
      + rebet + clear);
  }

  /**
   * The LAST strip: the pocket the ball called last at this table, or
   * nothing while the table has spun no wheel the reader saw (R2117).
   */
  private lastStrip(ctx: GameCtx, table: CasinoTable): string {
    const res = ctx.slice.result;
    if (!res || res.tableId !== table.id || res.aborted
      || res.game !== CasinoGame.CASINO_GAME_ROULETTE) return '';
    const n = res.rouletteNumber;
    return lastPart(n, pocketWord(n), S.roulette_last);
  }

  /**
   * The one notice a tap raised, the sentence the pick waits for, or,
   * before the first tap of a two-tap kind, the sentence that says it
   * takes two taps (R2143). `open` is the betting window: a shut window
   * takes no tap, so it states no hint.
   */
  private noticeLine(open: boolean): string {
    if (this.notice === 'not-adjacent') {
      return `<span class="notice bad">${S.roulette_not_adjacent}</span>`;
    }
    if (this.pick) {
      return '<span class="notice">'
        + `${S.roulette_pick_second({ number: pocketLabel(this.pick.first) })}</span>`;
    }
    const hint = open ? TWO_TAPS.get(INSIDE[this.inside]?.kind ?? -1) : undefined;
    return hint ? `<span class="notice hint">${hint}</span>` : '';
  }

  /** The chip sizes. A chip the reader cannot pay is dead. */
  private chipRow(ctx: GameCtx, table: CasinoTable, open: boolean): string {
    const min = tableMinBet(ctx.rules, table);
    const cells = CHIP_STEPS.map((step, i) => {
      const amt = min * step;
      const dead = !open || amt <= 0 || amt > ctx.credits;
      return ctl('chip', amount(amt), !dead, `chip${i === this.chip ? ' on' : ''}`,
        ` data-n="${i}" aria-pressed="${i === this.chip}"`);
    }).join('');
    return part('chips', 'class="casino-bar chips"', cells
      + `<span class="purse">${S.casino_you_hold({ amount: amount(ctx.credits) })}</span>`);
  }

  /** The five inside kinds, each with what the catalog says it pays. */
  private kindRow(ctx: GameCtx, open: boolean): string {
    const cells = INSIDE.map((row, i) => {
      const pays = paysOf(ctx.rules, row.kind);
      // A two-tap kind says so on its pill too, as its title (R2143).
      const two = TWO_TAPS.get(row.kind);
      return ctl('kind', `${row.label}<span class="pays">${S.roulette_pays({ pays })}</span>`,
        open, `kind${i === this.inside ? ' on' : ''}`,
        ` data-n="${i}" aria-pressed="${i === this.inside}"${two ? ` title="${two}"` : ''}`);
    }).join('');
    return part('kinds', 'class="casino-bar kinds"', cells);
  }

  /**
   * The two zeros and the three columns of twelve rows.
   *
   * A cell states what rides on its pocket: every inside bet that covers
   * the number, added up. That is what the cell is worth if the ball
   * stops there, and the list below still counts each bet one time.
   */
  private grid(
    ctx: GameCtx, bets: readonly CasinoBet[], live: boolean, dim: boolean, flash: number, hit: number,
  ): string {
    const row = INSIDE[this.inside];
    // A zero stands in no street, so it can carry a straight and a split
    // alone.
    const zeroDead = !live
      || (row?.kind !== K_STRAIGHT && row?.kind !== K_SPLIT);
    // `hit` is the pocket of the ball once it is down, and its cell wears
    // the win flash of that result (R2115); -1 marks no cell.
    const at = (n: number, on: boolean): string => this.cell(n, bets, on, n === hit ? flash : 0);
    // The double zero stands beside the zero on the wheel the pack states
    // (R2752); a wheel of one zero draws the zero alone.
    const pockets = pocketsOf(ctx.rules);
    // The zeros share the top row of the cloth in one box, half each, so
    // the zero stands over 1 and 2 and the double zero over 2 and 3.
    const zeros = at(0, !zeroDead) + (pockets > DOUBLE_ZERO ? at(DOUBLE_ZERO, !zeroDead) : '');
    const cells: string[] = [`<div class="zeros">${zeros}</div>`];
    for (let r = 1; r <= 12; r++) {
      for (let c = 1; c <= 3; c++) cells.push(at((r - 1) * 3 + c, live));
    }
    // `dim` is the window that has shut. The cells stay where they are and
    // the class takes them back, because the wheel above must have the
    // reader's eye and a grid that moved would move a target (R988).
    //
    // The outside bets stand IN the cloth, as on a classic felt (R2117):
    // the even-money bets and the dozens in two columns beside the twelve
    // rows they cover, and the columns under the column they name. The
    // stylesheet places each one by its `data-op` and its `data-n`, so the
    // whole cloth is one grid and about one screen tall on a telephone.
    return part('grid', `class="bet-grid cloth${dim ? ' dim' : ''}"`,
      cells.join('') + this.outside(ctx, bets, live));
  }

  /** One cell of the number grid. */
  private cell(n: number, bets: readonly CasinoBet[], live: boolean, flash = 0): string {
    const col = pocketColour(n);
    const cls = col === 'black' ? 'blk' : col;
    const marked = this.pick?.first === n ? ' pick' : '';
    const hit = flash > 0 ? ' hit' : '';
    const rides = ridesOn(bets, n);
    const chips = rides > 0 ? `<span class="rides">${amount(rides)}</span>` : '';
    return ctl('cell', `<span class="n">${pocketLabel(n)}</span>${chips}`, live,
      `bet-cell ${cls}${marked}${hit}`, ` data-n="${n}"${flash > 0 ? ` data-flash="${flash}"` : ''}`);
  }

  /** The dozens, the columns and the six even-money bets, as cells of the cloth. */
  private outside(ctx: GameCtx, bets: readonly CasinoBet[], live: boolean): string {
    // The stack on an outside button is read with the wire's own bet
    // target — the index for a dozen and a column, and nothing for an
    // even-money bet — because that is what the frame carries back.
    const indexed = (op: string, kind: number): string => [1, 2, 3].map((i) => (
      this.wide(op, i, KIND_LABEL[op], coverText(kind, [i]),
        stackOn(bets, kind, [i]), paysOf(ctx.rules, kind), live)
    )).join('');
    const evens = EVEN_ORDER.map((i) => {
      const row = EVENS[i];
      return this.wide('even', i, row.label, '',
        stackOn(bets, row.kind, NO_NUMBERS), paysOf(ctx.rules, row.kind), live);
    }).join('');
    return evens + indexed('dozen', K_DOZEN) + indexed('column', K_COLUMN);
  }

  /** One large outside button. */
  private wide(
    op: string, n: number, label: string, sub: string,
    stack: number, pays: number, live: boolean,
  ): string {
    const chips = stack > 0 ? `<span class="rides">${amount(stack)}</span>` : '';
    const under = sub ? `<span class="span">${sub}</span>` : '';
    return ctl(op, `<span class="n">${label}</span>${under}`
      + `<span class="pays">${S.roulette_pays({ pays })}</span>${chips}`,
    live, 'bet-cell wide', ` data-n="${n}"`);
  }

  /**
   * What stands on the felt, one row a bet. A row is a button: a press
   * takes that bet back while the window is open (R2117). The total and
   * CLEAR stand in the pinned stake line.
   */
  private betList(table: CasinoTable, bets: readonly CasinoBet[], open: boolean): string {
    const live = open && casinoOffers(table, CasinoAction.CASINO_ACTION_TAKE_BACK_BET);
    const last = bets.length - 1;
    const rows = bets.map((b, i) => {
      // A kind this build has no word for keeps its wire code, as
      // `moduleGradeWord` does, and never reads as another kind (R1361).
      const code = CASINO_BET_KINDS[b.kind]?.code ?? String(b.kind);
      const label = KIND_LABEL[code] ?? code;
      const on = coverText(b.kind, b.numbers);
      return ctl('takeback', `<span class="kind">${label}</span>`
        + `<span class="on">${on}</span>`
        + `<span class="amt">${amount(b.amount)}</span>`
        + `<span class="tb">${S.roulette_take_back}</span>`,
      // The last row names the key that takes it back (R2143).
      live, 'bet-row', ` data-n="${i}"${i === last ? keyTitle(S.roulette_take_back, 'casino_take_back') : ''}`);
    }).join('');
    return part('bets', 'class="bets"', rows);
  }

  /**
   * The wheel, as ONE part (`gameview.ts`).
   *
   * The markup changes only when the pocket the ball runs at changes, so
   * the browser runs the travel to its end while the countdown beside it
   * steps four times a second.
   */
  private wheel(ctx: GameCtx, table: CasinoTable, res: CasinoResult | null, rest = false): string {
    const pockets = pocketsOf(ctx.rules);
    return wheelPart({
      pockets,
      number: res ? res.rouletteNumber : null,
      handId: res ? res.handId : table.handId,
      // A wheel at rest stands on its pocket with no travel (R2198).
      still: ctx.still || rest,
    });
  }

}

/**
 * The seats on the rim of the wheel and the club badge at its hub
 * (R2165). The ring holds as many seats as the lobby row of this table
 * states; a table the lobby does not list yet holds the highest seat it
 * shows.
 */
function rimOf(ctx: GameCtx, table: CasinoTable): string {
  const row = ctx.slice.lobby?.tables.find((t) => t.id === table.id);
  const highest = table.seats.reduce((m, s) => Math.max(m, s.seat + 1), 0);
  const count = Math.max(row?.seats ?? 0, highest);
  const seats: RimSeat[] = table.seats.map((s) => ({
    seat: s.seat,
    name: seatName(s),
    badge: factionBadge(s.faction, CASINO_SEAT_BADGE_PX),
    stake: s.bet > 0 ? amount(s.bet) : '',
    you: s.isYou,
  }));
  return rimPart(seats, count, factionBadge(clubOf(ctx.slice.lobby), CASINO_HUB_BADGE_PX));
}

/**
 * The called number, its colour word, and what the reader took.
 *
 * The part stands as long as the wheel does, EMPTY while the ball is
 * still in the air: a part that came and went would change the shape of
 * the screen and take the wheel with it (R988).
 *
 * The reader's own move is the result tag of `moment.ts` (R2115). A spin
 * the reader put no bet on draws no result tag and no success ink: the row says
 * that there was no bet, because a move of 0 with no stake is neither a
 * win nor a loss. The row is the result line the live region says.
 */
function calledRow(ctx: GameCtx, table: CasinoTable, res: CasinoResult | null): string {
  if (!res) return part('called', '', '');
  const n = res.rouletteNumber;
  const col = pocketColour(n);
  // A zero is green, and it wears its own word, because a reader who
  // sees no colour word cannot tell a dead spin from a lost one.
  const word = pocketWord(n);
  const seq = ctx.slice.resultSeq;
  const ball = S.roulette_ball_on({ number: pocketLabel(n) });
  const head = `<span class="ball">${ball}</span>`
    + (word ? `<span class="word">${word}</span>` : '');
  const said = word ? `${ball} ${word}` : ball;
  if (casinoOwnRows(res, table).length === 0) {
    return part('called', `class="casino-bar called ${col}"${sayAttrs(seq, `${said}. ${S.roulette_no_bet}`)}`,
      `${head}<span class="nobet">${S.roulette_no_bet}</span>`);
  }
  const delta = res.yourDelta;
  const what = handWord(delta);
  return part('called', `class="casino-bar called ${col}"${sayAttrs(seq, `${said}. ${tagSpeech(delta, what)}`)}`,
    `${head}<span class="delta">${resultTag(delta, what, seq)}</span>`);
}

/**
 * Which pockets one bet covers, in the shortest form that stays true.
 *
 * `numbers` is the bet TARGET the wire carries, and not the cover: an
 * inside bet names its one to six pockets, a dozen and a column name
 * their index, and every other outside kind names nothing. So the two
 * indexed kinds derive their pockets here, for the picture alone.
 *
 * A dozen is a span, because its pockets run without a gap. A column
 * steps by three, so a span would say something false and the head of the
 * column is stated instead. An even-money bet states no pocket: its label
 * already says which half of the felt it takes.
 */
export function coverText(kind: number, numbers: readonly number[]): string {
  if (CASINO_BET_KINDS[kind]?.inside) return numbers.map(pocketLabel).join('·');
  const idx = numbers[0] ?? 0;
  if (idx < 1 || idx > 3) return '';
  if (kind === K_DOZEN) return `${(idx - 1) * 12 + 1}–${idx * 12}`;
  if (kind === K_COLUMN) return `${idx}·${idx + 3}·${idx + 6}…`;
  return '';
}

/** The screen the panel mounts. One instance holds the bet in build. */
export const rouletteView: GameView = new RouletteView();
